import clsx from "clsx";
import React, { ReactNode, useEffect, useState } from "react";
import { GridCellRenderer, MultiGrid } from "react-virtualized";
import styled from "@emotion/styled";
import { HeaderCell } from "./HeaderCell";
import {
  DetailsListColumn,
  DetailsListCustomCellProps,
  DetailsListHoverState,
  DetailsListProps
} from "./types";

type Props = {
  columns: DetailsListColumn[];
  rows: any[];
  sort: DetailsListProps["sort"];
  onClickHeaderCell: DetailsListProps["onClickHeader"];
  onClickCell: DetailsListProps["onClickCell"];
  refGrid: React.MutableRefObject<MultiGrid | null | undefined>;
  onResizeColumn?: DetailsListProps["onResizeColumn"];
  cellClassName: DetailsListProps["cellClassName"];
};

export const useCellRenderer = ({
  columns,
  rows,
  sort,
  onClickHeaderCell,
  onClickCell,
  refGrid,
  onResizeColumn,
  cellClassName
}: Props) => {
  const [hover, setHover] = useState<DetailsListHoverState>({
    column: null,
    row: null,
    rowIndex: null,
    columnIndex: null
  });

  useEffect(() => {
    refGrid.current?.recomputeGridSize();
  }, [hover]);

  const cellRenderer: GridCellRenderer = gridCellProps => {
    const { columnIndex, rowIndex, key, style } = gridCellProps;
    const column = columns[columnIndex];
    const row = rows[rowIndex - 1];

    let align = "center";
    if (column.align) align = column.align;
    if (column.align === "left") align = "flex-start";
    if (column.align === "right") align = "flex-end";

    if (rowIndex === 0) {
      return (
        <HeaderCell
          key={key}
          column={column}
          sort={sort}
          onClickHeaderCell={onClickHeaderCell}
          gridCellProps={gridCellProps}
          columnIndex={columnIndex}
          onResizeColumn={onResizeColumn}
        />
      );
    } else {
      const customCellProps: DetailsListCustomCellProps = {
        column,
        row,
        columnIndex,
        rowIndex: rowIndex - 1
      };

      const truncateProps = column.truncateProps
        ? column.truncateProps(customCellProps)
        : undefined;

      let toRender: ReactNode = row ? (
        <Truncate {...truncateProps}>{row[column.key]}</Truncate>
      ) : null;

      if (column.render) {
        const customRender = column.render(customCellProps);
        toRender = customRender;
      } else if (column.transform) {
        toRender = (
          <Truncate {...truncateProps}>
            {column.transform(customCellProps)}
          </Truncate>
        );
      }

      const className = clsx("cell", {
        odd: (rowIndex - 1) % 2 !== 0,
        even: (rowIndex - 1) % 2 === 0,
        hover: hover.rowIndex === rowIndex,
        ["default-render"]: !column.render
      });

      let customCellClassName;
      if (cellClassName) {
        customCellClassName = cellClassName(customCellProps);
      }

      return (
        <Cell
          key={key}
          style={style}
          className={clsx([className, customCellClassName])}
          data-columnindex={columnIndex}
          data-rowindex={rowIndex}
          data-columnkey={column.key}
          onMouseEnter={() => {
            setHover({
              column: column,
              row,
              columnIndex: columnIndex,
              rowIndex
            });
          }}
          onMouseLeave={() => {
            setHover({
              column: null,
              row: null,
              columnIndex: null,
              rowIndex: null
            });
          }}
          onClick={() =>
            onClickCell &&
            onClickCell({
              column: column,
              row,
              columnIndex: columnIndex,
              rowIndex
            })
          }
          justifyContent={align}
        >
          {toRender}
        </Cell>
      );
    }
  };

  return cellRenderer;
};

const Cell = styled.div<{ justifyContent: string }>`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12px;
  border-bottom: 1px solid #f3f2f1;
  box-sizing: border-box;
  &.hover {
    background-color: #f3f2f1;
  }
  &.default-render {
    display: flex;
    align-items: center;
    padding: 0 12px;
    height: 100%;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    justify-content: ${p => p.justifyContent};
  }
`;

const Truncate = styled.div`
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`;

Truncate.defaultProps = { className: "truncate" };
